import React, { useState } from 'react';
import { useFormik } from 'formik';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import * as Yup from 'yup';

import { getErrorMessage } from '@utils/validation';
import { getLinkDataFromUrl, getRouteUrl } from '@utils/urls';
import request from '@utils/request';

import { StaticCard } from 'app/common/components/CardBack/StaticCard';
import FlipCardModal from 'app/common/components/FlipCardModal';
import { REQUIRED_FIELD_ERROR } from '@common/constants/formValidation';
import { MASKED_INPUT_TYPE } from '@common/constants/maskedInput';
import { TextInputMasked } from '@common/components/forms';
import Layout from '@common/containers/Layout';
import RouteLayout from '@common/containers/RouteLayout';
import * as endpoints from '@common/endpoints';
import {
  PRE_LOGIN,
  REGISTER,
  VIP_CARD_ALREADY_ACTIVATED,
  VIP_CARD_INVALID_EMAIL,
  VIP_CARD_LOGIN,
  VIP_CARD_NOT_YET_ACTIVATED,
  VIP_INVALID_CARD,
} from '@common/routes';
import { VIP_CARD, VIP_CARD_BACK } from '@common/images';

import {
  Description,
  FormColumn,
  FormRow,
  HowToButton,
  StyledFormControl,
  SubmitButton,
} from './VIPCardLogin.styled';

const blockOptions = {
  0: {
    from: 0,
    mask: global.IMask.MaskedRange,
    maxLength: 1,
    placeholderChar: '0',
    to: 9,
  },
};

const getErrorRedirect = error => {
  let statusCode = 400;

  if (error.response.data) {
    ({ statusCode } = error.response.data);
  }

  switch (statusCode) {
    case 400:
      return VIP_CARD_ALREADY_ACTIVATED;
    case 412:
      return VIP_CARD_NOT_YET_ACTIVATED;
    case 428:
      return VIP_CARD_INVALID_EMAIL;
    default:
      return VIP_INVALID_CARD;
  }
};

export function VIPCardLogin() {
  const router = useRouter();
  const { t } = useTranslation('common');
  const pageUrl = getRouteUrl(router.locale, VIP_CARD_LOGIN);
  const [isVIPCardModalOpen, setIsVIPCardModalOpen] = useState(false);

  const formik = useFormik({
    initialValues: {
      cardNr: '',
    },
    onSubmit: async ({ cardNr }) => {
      try {
        const {
          data: { signedCustomerId },
        } = await request.get(endpoints.cardEmail(cardNr.replace(/ /g, '')));

        router.push(
          {
            pathname: REGISTER,
            query: {
              signedCustomerId,
            },
          },
          `${getRouteUrl(router.locale, REGISTER)}?signedCustomerId=${signedCustomerId}`,
        );
      } catch (error) {
        if (error?.response?.data?.redirect) {
          const { urlObject } =
            getLinkDataFromUrl(error.response.data.redirect) || {};

          if (urlObject) {
            router.push(
              urlObject,
              getRouteUrl(router.locale, urlObject.pathname, urlObject.query),
            );
          }
        }

        const staticRedirect = getErrorRedirect(error);

        if (error?.response?.data?.friendlyError) {
          router.push(
            {
              pathname: staticRedirect,
              query: {
                cardNr,
                errorFallback: error.response.data.friendlyError,
              },
            },
            getRouteUrl(router.locale, staticRedirect),
          );
        }
      }
    },
    validationSchema: VIPCardLogin.validationSchema,
  });

  const {
    values,
    handleSubmit,
    handleBlur,
    touched,
    errors,
    isSubmitting,
    setFieldValue,
  } = formik;

  return (
    <React.Fragment>
      <FlipCardModal
        isOpen={isVIPCardModalOpen}
        onClose={() => setIsVIPCardModalOpen(false)}
        imageBack={
          <StaticCard
            image={{
              src: VIP_CARD_BACK,
              alt: 'VIP-Karte',
            }}
          />
        }
        imageFront={
          <StaticCard
            image={{
              src: VIP_CARD,
              alt: 'VIP-Karte',
            }}
          />
        }
      />
      <Layout
        title={t('vipCardLoginMetaTitle', 'VIP-Karte anmelden')}
        canonicalPageUrl={pageUrl}
      >
        <RouteLayout
          backRoute={PRE_LOGIN}
          backRouteAs={getRouteUrl(router.locale, PRE_LOGIN)}
          title={t('vipCardLoginTitle', 'Mit VIP-Karte anmelden')}
          stickyTitle={t('vipCardLoginTitle', 'Mit VIP-Karte anmelden')}
          columns={8}
        >
          <Description>
            {t(
              'vipCardLoginDescription',
              'Falls Du 14 Tage nach der Anmeldung im Store noch keine E-Mail erhalten hast, gib hier Deine VIP-Kartennummer ein, um Dein Online-Login zu erstellen.',
            )}
          </Description>
          <HowToButton onClick={() => setIsVIPCardModalOpen(true)}>
            {t('whereFindCustomerNumber', 'Wo finde ich meine Kundenummer?')}
          </HowToButton>
          <form onSubmit={handleSubmit} noValidate>
            <FormRow>
              <FormColumn>
                <StyledFormControl
                  label={t('vipCardForm.cardNrLabel', 'Kundennummer')}
                  hasError={touched.cardNr && !!errors.cardNr}
                  message={
                    touched.cardNr &&
                    getErrorMessage(t, errors.cardNr, {
                      [REQUIRED_FIELD_ERROR]: {
                        defaultValue: 'Please enter a number',
                        key: 'vipCardForm.cardNumberRequiredError',
                      },
                      cardNumberLengthError: {
                        defaultValue: 'You must provide 13 numbers',
                        key: 'vipCardForm.cardNumberLengthError',
                      },
                    })
                  }
                >
                  <TextInputMasked
                    name="cardNr"
                    value={values.cardNr}
                    mask={t(
                      'vipCardForm.cardNrPlaceholder',
                      '0 0000 0000 0000',
                    )}
                    blocks={blockOptions}
                    unmask
                    lazy={false}
                    overwrite
                    autofix
                    onChange={value => setFieldValue('cardNr', value)}
                    onBlur={handleBlur}
                    maskedInputType={MASKED_INPUT_TYPE.NUMBER}
                    novalidate
                    hasError={touched.cardNr && !!errors.cardNr}
                    disabled={isSubmitting}
                  />
                </StyledFormControl>
              </FormColumn>
            </FormRow>
            <SubmitButton
              type="submit"
              disabled={isSubmitting}
              variant="primaryBig"
            >
              {t('vipCardLogin.submit', 'Anmelden')}
            </SubmitButton>
          </form>
        </RouteLayout>
      </Layout>
    </React.Fragment>
  );
}

VIPCardLogin.validationSchema = Yup.object().shape({
  cardNr: Yup.string()
    .trim()
    .transform(value => value.replace(/ /g, ''))
    .length(13, 'cardNumberLengthError')
    .required(REQUIRED_FIELD_ERROR),
});

export default VIPCardLogin;
